import type { z } from "zod";
import {
  memberSchema,
  newsletterSchema,
  opportunitySchema,
  submissionSchema,
} from "@/lib/schemas/operations";
import {
  auditEventSchema,
  dashboardSchema,
  dashboardOperationsSchema,
  dashboardPreferencesSchema,
  dashboardSiteAnalyticsSchema,
  mediaDetailSchema,
  mediaSchema,
  memberDetailSchema,
  notificationPreferenceSchema,
  notificationSchema,
  panelSessionSchema,
  panelUserDetailSchema,
  panelUserRowSchema,
  previewTicketSchema,
  rolePackageSchema,
  savedViewSchema,
  submissionDetailSchema,
} from "@/lib/schemas/operations";
import type {
  AuditEvent,
  Dashboard,
  DashboardOperations,
  DashboardPreferences,
  DashboardSiteAnalytics,
  MediaAsset,
  MediaAssetDetail,
  Member,
  MemberDetail,
  NewsletterSubscriber,
  NotificationPreference,
  OpportunityListItem,
  PanelNotification,
  PanelSession,
  PanelUserDetail,
  PanelUserRow,
  PreviewTicket,
  RolePackage,
  SavedView,
  Submission,
  SubmissionDetail,
} from "@/lib/domain/operations";

const date = (value: string) => new Date(value);
const nullableDate = (value: string | null) => (value ? new Date(value) : null);
export const toMember = (row: z.infer<typeof memberSchema>): Member => ({
  id: row.id,
  name: row.name,
  email: row.email,
  phone: row.phone,
  jobTitle: row.job_title,
  locale: row.locale,
  status: row.status,
  statusLabel: row.status_label,
  emailVerifiedAt: nullableDate(row.email_verified_at),
  createdAt: date(row.created_at),
  updatedAt: date(row.updated_at),
});
export const toMemberDetail = (
  row: z.infer<typeof memberDetailSchema>,
): MemberDetail => ({
  ...toMember(row),
  organizations: row.organizations.map((item) => ({
    id: item.id,
    organizationId: item.organization_id,
    name: item.name,
    role: item.role,
    roleLabel: item.role_label,
    primary: item.is_primary,
    active: item.is_active,
  })),
  grants: row.grants.map((item) => ({
    id: item.id,
    opportunityId: item.opportunity_id,
    opportunity: item.opportunity,
    expiresAt: nullableDate(item.expires_at),
    active: item.active,
  })),
  submissions: row.submissions.map((item) => ({
    id: item.id,
    kind: item.kind,
    submittedAt: date(item.submitted_at),
    subject: item.subject,
  })),
});
export const toOpportunityListItem = (
  row: z.input<typeof opportunitySchema>,
): OpportunityListItem => ({
  id: row.id,
  code: row.code,
  title: row.title,
  summary: row.summary,
  location: row.location,
  accessPreset: row.access_preset,
  closesAt: nullableDate(row.closes_at),
  grantCount: row.grant_count,
  lifecycleStatus: row.lifecycle_status || "active",
  publicationStatus: row.publication_status || "draft",
  hasLiveVersion: row.has_live_version || false,
  kindLabel: row.kind_label || "",
  sectors: row.sectors || [],
  requestCount: row.request_count || 0,
  saverCount: row.saver_count || 0,
});
export const toSubmission = (
  row: z.infer<typeof submissionSchema>,
): Submission => ({
  id: row.id,
  kind: row.kind,
  kindLabel: row.kind_label || "",
  name: row.name,
  companyName: row.company_name,
  email: row.email,
  phone: row.phone,
  subject: row.subject,
  message: row.message,
  locale: row.locale,
  submittedAt: date(row.submitted_at),
  status: row.status,
  statusLabel: row.status_label,
  internalNote: row.internal_note,
  crmReference: row.crm_reference,
  documentCount: row.document_count,
  context: row.context,
});
export const toSubmissionDetail = (
  row: z.infer<typeof submissionDetailSchema>,
): SubmissionDetail => ({
  ...toSubmission(row),
  answerSections: row.answer_sections,
  payload: row.payload,
  documents: row.documents.map((item) => ({
    id: item.id,
    name: item.name,
    mimeType: item.mime_type,
    size: item.size,
    scanStatus: item.scan_status,
    downloadable: item.downloadable,
  })),
});
export const toNewsletterSubscriber = (
  row: z.infer<typeof newsletterSchema>,
): NewsletterSubscriber => ({
  id: row.id,
  email: row.email,
  locale: row.locale,
  status: row.status,
  statusLabel: row.status_label,
  source: row.source,
  sourceLabel: row.source_label,
  createdAt: date(row.created_at),
  confirmedAt: nullableDate(row.confirmed_at),
  unsubscribedAt: nullableDate(row.unsubscribed_at),
});
export const toMediaAsset = (row: z.infer<typeof mediaSchema>): MediaAsset => ({
  id: row.id,
  kind: row.kind,
  name: row.name,
  mimeType: row.mime_type,
  size: row.size,
  url: row.url,
  focalX: row.focal_x,
  focalY: row.focal_y,
  source: row.source,
  usageCount: row.usage_count,
  createdAt: date(row.created_at),
});
export const toMediaAssetDetail = (
  row: z.infer<typeof mediaDetailSchema>,
): MediaAssetDetail => ({
  id: row.id,
  kind: row.kind,
  name: row.name,
  mimeType: row.mime_type,
  size: row.size,
  url: row.url,
  focalX: row.focal_x,
  focalY: row.focal_y,
  source: row.source,
  createdAt: date(row.created_at),
  translations: Object.fromEntries(
    Object.entries(row.translations).map(([key, value]) => [
      key,
      { title: value.title, altText: value.alt_text },
    ]),
  ),
  usages: row.usages.map((usage) => ({
    type: usage.type,
    typeLabel: usage.type_label,
    target: usage.target,
    field: usage.field,
    fieldLabel: usage.field_label,
    href: usage.href,
  })),
});
export const toPanelUserRow = (
  row: z.infer<typeof panelUserRowSchema>,
): PanelUserRow => ({
  id: row.id,
  email: row.email,
  name: row.name,
  role: row.role,
  roleLabel: row.role_label,
  overrideCount: row.override_count,
  manageable: row.manageable,
  passwordResettable: row.password_resettable,
  overridesEditable: row.overrides_editable,
  active: row.is_active,
  lastLogin: nullableDate(row.last_login),
  totpConfigured: row.totp_configured,
});
export const toPanelUserDetail = (
  row: z.infer<typeof panelUserDetailSchema>,
): PanelUserDetail => ({
  ...toPanelUserRow(row),
  overrides: row.overrides,
  availableCapabilities: row.available_capabilities.map((item) => ({
    key: item.key,
    label: item.label,
    includedInRole: item.included_in_role,
  })),
  sessions: row.sessions.map((item) => ({
    id: item.id,
    userAgent: item.user_agent,
    ipHint: item.ip_hint,
    startedAt: date(item.started_at),
    lastSeenAt: date(item.last_seen_at),
    expiresAt: date(item.expires_at),
    revokedAt: nullableDate(item.revoked_at),
    active: item.active,
  })),
});
export const toRolePackage = (
  row: z.input<typeof rolePackageSchema>,
): RolePackage => ({
  key: row.key,
  label: row.label,
  description: row.description,
  userCount: row.user_count,
  editable: row.editable ?? false,
  assignable: row.assignable ?? false,
  capabilities: row.capabilities,
});
export const toPanelSession = (
  row: z.infer<typeof panelSessionSchema>,
): PanelSession => ({
  id: row.id,
  current: row.current,
  userAgent: row.user_agent,
  startedAt: date(row.started_at),
  lastSeenAt: date(row.last_seen_at),
  expiresAt: date(row.expires_at),
  revokedAt: nullableDate(row.revoked_at),
});
export const toNotification = (
  row: z.infer<typeof notificationSchema>,
): PanelNotification => ({
  id: row.id,
  title: row.title,
  message: row.message,
  severity: row.severity,
  targetPath: row.target_path,
  readAt: nullableDate(row.read_at),
  createdAt: date(row.created_at),
});
export const toNotificationPreference = (
  row: z.infer<typeof notificationPreferenceSchema>,
): NotificationPreference => ({
  eventKey: row.event_key,
  label: row.label,
  required: row.required,
  inAppEnabled: row.in_app_enabled,
  emailEnabled: row.email_enabled,
});
export const notificationPreferencePayload = (row: NotificationPreference) => ({
  event_key: row.eventKey,
  label: row.label,
  required: row.required,
  in_app_enabled: row.inAppEnabled,
  email_enabled: row.emailEnabled,
});
export const toAuditEvent = (
  row: z.infer<typeof auditEventSchema>,
): AuditEvent => ({
  id: row.id,
  action: row.action,
  actionLabel: row.action_label,
  actor: row.actor,
  target: row.target,
  success: row.success,
  result: row.result,
  changes: row.changes,
  createdAt: date(row.created_at),
});
export const toDashboard = (
  row: z.infer<typeof dashboardSchema>,
): Dashboard => ({
  cards: row.cards,
  queues: row.queues,
  recent: row.recent.map((item) => ({
    id: item.id,
    label: item.label,
    domain: item.domain,
    domainLabel: item.domain_label,
    locale: item.locale,
    status: item.status,
    statusLabel: item.status_label,
    updatedAt: date(item.updated_at),
  })),
  allowedWidgets: row.allowed_widgets,
  unreadNotifications: row.unread_notifications,
  generatedAt: date(row.generated_at),
});
const toDashboardMetric = (row: z.infer<typeof dashboardOperationsSchema>["kpis"][number]) => ({
  key: row.key,
  label: row.label,
  value: row.value,
  previousValue: row.previous_value,
  changePercent: row.change_percent,
  href: row.href,
});
export const toDashboardOperations = (
  row: z.infer<typeof dashboardOperationsSchema>,
): DashboardOperations => ({
  range: row.range,
  compare: row.compare,
  start: row.start,
  end: row.end,
  generatedAt: date(row.generated_at),
  kpis: row.kpis.map(toDashboardMetric),
  series: row.series,
  breakdowns: {
    submissionStatus: row.breakdowns.submission_status,
    workflowStatus: row.breakdowns.workflow_status,
    memberStatus: row.breakdowns.member_status,
  },
  health: row.health,
});
export const toDashboardSiteAnalytics = (
  row: z.infer<typeof dashboardSiteAnalyticsSchema>,
): DashboardSiteAnalytics => ({
  status: row.status,
  generatedAt: date(row.generated_at),
  stale: row.stale,
  message: row.message,
  range: row.range,
  compare: row.compare,
  start: row.start,
  end: row.end,
  kpis: row.kpis.map(toDashboardMetric),
  traffic: row.traffic,
  sources: row.sources,
  devices: row.devices,
  topPages: row.top_pages,
});
export const toDashboardPreferences = (
  row: z.infer<typeof dashboardPreferencesSchema>,
): DashboardPreferences => ({
  widgets: row.widgets,
  defaultRange: row.default_range,
  version: row.version,
});
export const dashboardPreferencesPayload = (row: DashboardPreferences) => ({
  widgets: row.widgets,
  default_range: row.defaultRange,
  version: row.version,
});
export const toSavedView = (
  row: z.infer<typeof savedViewSchema>,
): SavedView => ({
  id: row.id,
  name: row.name,
  configuration: row.configuration,
  default: row.is_default,
});
export const toPreviewTicket = (
  row: z.infer<typeof previewTicketSchema>,
): PreviewTicket => ({ url: row.url, expiresAt: date(row.expires_at) });
